<!--

Copyright Paul B. Lucas (C) 2025  ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is rendered when user (as an authenticated department approver, supervisor, or system admin) selects the 
VIEW AGREEMENTS route on the initial splash page.  Agreements are listed with significant properties in a table row format
if the @media probe returns a screen size > MOBILE.  If MOBILE size is detected, then agreement 'rows' are rendered in  a box 
container with alternating label:value rows in a single column.   Actions are rendered as buttons - three to a column.

Actions available on this component for authorized users (Department and Sys Admins) are  {APPROVE | PEND | REJECT} 

Revisions:
    02/25/2025      pblucas     initial creation
 

-->
<template>
    <div class="_master_container">
        <div v-if="renderState=='LIST'">
            <div v-if="filteredAgreements?.length  && filteredAgreements.length !== 0">
                <div class="_list">
                    <p class="_p_page_header">Agreements for org codes: {{ orgDisplay }}</p>
                </div>
                 <div class="_list_container">
                    <div class="_agreements_table">
                        <!-- display row of filter inputs -->
                        <div class="_agreements_table_row">
                            <div class="_agreements_table_cell">
                                <input class="_input_filter" type="text" v-model="filters['number']" @keyup="setFilters">
                            </div> 
                             <div class="_agreements_table_cell">
                                <input class="_input_filter" type="text" v-model="filters['employee']" @keyup="setFilters">
                            </div> 
                            <div class="_agreements_table_cell">
                                <input class="_input_filter" type="text" v-model="filters['org']" @keyup="setFilters">
                            </div> 
                            <div class="_agreements_table_cell">
                                <input class="_input_filter" type="text" v-model="filters['date']" @keyup="setFilters">
                            </div> 
                            <div class="_agreements_table_cell">
                                <input class="_input_filter" type="text" v-model="filters['state']" @keyup="setFilters">
                            </div> 
                            <div class="_agreements_table_cell">
                                <button class="_button_clear_filters" @click="clearFilters">Clear Filters</button>
                            </div> 
                        </div>

                        <!-- header row-->
                        <div class="_agreements_table_row">
                            <div class="_agreements_table_cell">
                                <p class="_p_list_header">Number</p>
                            </div> 
                            <div class="_agreements_table_cell">
                                <p class="_p_list_header">Employee</p>
                            </div> 
                            <div class="_agreements_table_cell">
                                <p class="_p_list_header">Orgcode</p>
                            </div> 
                            <div class="_agreements_table_cell">
                                <p class="_p_list_header">Submittal Date</p>
                            </div>
                            <div class="_agreements_table_cell">
                                <p class="_p_list_header">Asset State</p>
                            </div>
                            <div class="_agreement_actions_table_cell_header">
                                <!-- per agreement actions -->
                                <p class="_p_list_actions_header">Actions</p>
                            </div>
                        </div>
                    
                        <!-- table row for acctual values - number value is link -->
                        <div class="_agreements_table_row" v-for="(_,idx) in filteredAgreements" :key="filteredAgreements.length">
                            <div class="_agreements_table_link_cell" style="text-align: left;"            
                                tabindex="0"
                                aria-label="Action link to view in list"  
                                aria-describedby="Link on agreement number to open view page on agreement selected in list row"  
                                @click="viewAgreement(idx)"
                                @keydown.enter="viewAgreement(idx)">
                                {{ filteredAgreements[idx]['number'] }}
                            </div>
                            <div class="_agreements_table_cell" style="text-align: left;">
                                {{ filteredAgreements[idx]['employee-name'] }}
                            </div>   
                            <div class="_agreements_table_cell" style="text-align: left;">
                                {{ filteredAgreements[idx]['applicable-org'] }}
                            </div>                
                             <div class="_agreements_table_cell">
                                {{ prettyDate(filteredAgreements[idx]['date-submitted']) }}
                            </div>
                            <div class="_agreements_table_cell">
                                {{ filteredAgreements[idx]['asset-state'] }}
                            </div>
                            <div class="_agreement_actions_table_cell">
                                <button class="_action_button" style="order:1;" 
                                    tabindex="0" 
                                    aria-label="Item button to view listed agreement" 
                                    aria-describedby="Inline control on list row to view listed agreement" 
                                    @click.stop="viewAgreement(idx)"
                                    @keydown.enter.stop="viewAgreement(idx)">View</button>
                            </div>
                        </div>
                    </div>  <!-- end of table -->
                </div>      <!-- end of bordered container-->
            </div>
            <div class="_no_agreements_warning_container" v-else>
                <p class="_agreements_warning" style="order: 1;">
                    There are no agreements on the provided org list
                </p>
                <div class="_agreements_warning_clear" style="order: 2;">
                    <button class="_main_button" 
                        tabindex="0"
                        aria-label="Clears filter list" 
                        aria-decribedby="Allow user option of clearing filters when no results show on current filters" 
                        @click="clearFilters"
                        @keydown.enter="clearFilters">Clear Filters</button>
                </div> 
            </div>           
        </div>      <!-- end of list render -->

        <div class="_agree_view" v-if="filteredAgreements?.[viewIndex] && renderState=='VIEW'">
            <!-- render the ViewAgreementEntire View on selected agreement -->  
           <ViewAgreementEntire :key="filteredAgreements[viewIndex].uuid"  
                                :agreement="filteredAgreements[viewIndex]" 
                                :employee="employee"
                                :claims="claims"
                                :gat="gat" 
                                :config="config" 
                                :saveFlag="saveFlag"/> 
                                
        </div>

        <!-- section for import/export -->
        <div v-if="renderState == 'IMPORT'">
            <p class="_p_import">
                Because the agreement class has array properties, e.g., loaned assets, the optimal
                form for imports is to import from JSON.  Agreements may be imported from CSV files; however, import of 
                assets require a more complicated import format.  To 
                import assets, an auxilary table must be included in the spreadsheet and formatted according to
                the agreement import specification.  Refer to the user's guide for the necessary format.  In general, auxillary tables 
                are specified by placing a '+' in the first position of a row followed by the name of the auxilary table,
                e.g., [+ , assets] followed by the comma delimited column header names in the subsequent row.  Rows following 
                the header row are comma delimited values rows.    When auxillary tables are completed, a '-' character in the 
                first position of a row e.g., [ - ] will return processing to agreement and subsequent rows are treated as comma 
                delimited agreement values.
                <br /><br/>
                When an agreement is imported, an agreement object will be created with an unique UUID and id and 
                stored in the database.  Any imported agreement will be added to the database as a new Agreement
                and be assigned a new number and UUID.  If the imported agreement includes a UUID or agreement number,
                then if a database record having that UUID or number is present, the properties in the spreadsheet will 
                be used to update the database agreement rather than creating a new agreement.
                <br/><br/>
                <b>Note: </b>Any spreadsheet column label must be in the API collection dictionary as class
                properties in order for the values to be processed.  If not, values are skipped and not written 
                to agreement object.
                <br/><br/>
            </p>
          
            <br />
            <div class="_agreements_inline_controls">
                <!-- <button class="_main_button"       type="button" @click="selectXLSXFile()">Import from Excel XLSX</button> -->
                <button class="_main_button" type="button" 
                    tabindex="0"
                    aria-label="Import from CSV File" 
                    aria-describedby="Selects import file type to be CSV" 
                    @click="selectCSVFile()"
                    @keydown.enter="selectCSVFile()">Import from Excel CSV</button>
                <button class="_main_button" type="button" 
                    tabindex="0"
                    aria-label="import from JSON file" 
                    aria-describedby="Selects import file type to be JSON" 
                    @click="selectJSONFile()"
                    @keydown.enter="selectJSONFile()">Import from JSON</button>
                <button class="_main_button" type="button" 
                    tabindex="0"
                    aria-label="import from JSON file" 
                    aria-describedby="Selects import file type to be JSON" 
                    @click="renderState = 'LIST'"   
                    @keydown.enter="renderState = 'LIST'">Cancel</button>
             </div>
        </div>
        <div v-if="renderState == 'FILE'">
            <div v-if="processSelector == 'CSV'" class="_import_fieldset">     
                <div class="_row" style="order:1">
                    <div class="_col_30">
                        <p class="_p_import_right">
                            Select the type of import:
                        </p>
                    </div>
                    <div class="_col_70">
                        <div class="_scol_25">
                            <div class="_scol_15">
                                <input id="_import_type_principal" tabindex="0"
                                    aria-label="Agreement principal import"   
                                    aria-describedby="import_radio"   
                                    type="radio" name="import_radio" class="_radio" v-model="agreeImportType" value="agreements">
                            </div>
                            <div class="_scol_80">
                                <label class="_item_label">Agreement</label>
                            </div>
                        </div>
                         <div class="_scol_25">
                            <div class="_scol_15">
                                <input id="_import_type_asset" tabindex="2"
                                    aria-label="Agreement asset import"   
                                    aria-describedby="import_radio"   
                                    type="radio" name="import_radio" class="_radio" v-model="agreeImportType" value="assets">
                            </div>
                            <div class="_scol_50">
                                <label class="_item_label">Asset</label>
                            </div>
                        </div>
                        <div class="_scol_25">
                            <div class="_scol_15">
                                <input id="_import_type_consolidated" tabindex="3"
                                    aria-label="Agreement consolidated import"   
                                    aria-describedby="import_radio"   
                                    type="radio" name="import_radio" class="_radio" v-model="agreeImportType" value="consolidated">
                            </div>
                            <div class="_scol_80">
                                <label class="_item_label">Consolidated</label>
                            </div>
                        </div>
                    </div>
                 </div>   
                 <div class="_row" style="order:2">
                    <p class="_p_import_center" v-if="agreeImportType=='schedules' || agreeImportType=='assets'"> 
                        You have selected <span v-if="agreeImportType=='assets'">an</span><span v-else>a</span> {{agreeImportType}} 
                        auxilary file to import.  In order to associate the {{ agreeImporttype }} records with an agreement, 
                        you must include in the subordinate data row either an agreement number or agreement UUID to act as a key.
                    </p>
                    <p class="_p_import_center" v-if="agreeImportType=='consolidated'"> 
                        You have selected a {{agreeImportType}} file to import.  This file should 
                        be structured as rows of agreements with interspersed asset records
                        where the asset records are added using the +/- syntax.  Refer to the user manual
                        for details.
                    </p>
         
                </div>
                <div class="_row" style="order:3">
                    <p class="_p_import_center"> 
                    Select a file to import. Type is {{ processSelector }}. <br/><br/>
                    </p>
                    <div>
                        <input id="file_01" class="_file_selector" type="file" tabindex="-1" 
                            aria-label="File selection for import" 
                            aria-describedby="File to import in CSV or JSON format" 
                            accept=".csv,.xls,.xlsx,text/csv, application/json, application/vnd: ms-excel"  
                            @change="importSelection"/>
                    </div>
                </div>
            </div>  
            <div v-if="processSelector == 'JSON'" class="_import_fieldset">                     
                <div class="_row" style="order:13">
                    <p class="_p_import_center"> 
                    Select a file to import. Type is {{ processSelector }}. <br/><br/>
                    </p>
                    <div>
                        <input id="file_01" class="_file_selector" type="file" tabindex="-1" 
                            aria-label="File selection for import" 
                            aria-describedby="File to import in CSV or JSON format" 
                            accept=".csv,.xls,.xlsx,text/csv, application/json, application/vnd: ms-excel"  
                            @change="importSelection"/>
                    </div>
                </div>
            </div>  
        </div>

        <div class="_export_container" v-if="renderState == 'EXPORT'">
            <p class="_p_export">
                The agreement class has array properties, e.g. loaned assets.  Because spreadsheets are 
                flat in nature, an export to Excel will not normally include these array properties.  If the agreement 
                loaned assets are required, click the 'include array properties checkbox'.  This will provide a
                dropdown list of formats.  If the 'consolidated' option is selected, the array properties will be inserted 
                in-table, after the data row, otherwise if the 'individual' option is selected, a second worksheet 
                will be added to the Excel workbook.  If 'individual' reporting is requested, each auxiliary row  
                will include the agreement uuid as a foreign attribute with which to associate the record to the
                parent agreement. <br /><br />
 
                <b>Note</b> that each agreement an employee may have, either issued or in process, may have a different
                asset list, or may duplicate assets.  Duplicated assets are removed on administrative listings.  
                <br /><br/>

                JSON files will include all subordinate array properties.
            </p>
            <div class="_export_check" >
                <div class="_export_rules" style="order:1;">
                    <input id="export_prop1" class="_check" type="checkbox" v-model="bIncludeArrayProps" 
                        tabindex="0"
                        aria-label="Checkbox to include subordinate properties in EXCEL workbook" 
                        aria-describedby="When checked, includes additional tabs with array properties" 
                        value=true>
                    <label class="_check_label" for="export_prop1">Include all array properties (e.g. schedules or assets)</label>
                </div>
                <div class="_export_rules" style="order: 2;" v-if="bIncludeArrayProps">
                    <label class="_input_label" for="format_type">Select export formatting option</label>
                    <select id="format_type" class="_select" 
                        v-model="sExportFormat">
                        <option id="format_list" class="_type_list" v-for="(item) in aExportFormats" 
                            aria-label="Subordinate properties formatting options" 
                            aria-description="Dropdown list to select format option"
                            :value="item"> {{item}} 
                        </option>
                    </select>
                </div>
            </div>
            <br />
            <div class="_export_controls">
                <button class="_main_button"       type="button" 
                    tabindex="0" 
                    aria-label="Export agreements as Excel XLSX file"  
                    aria-decribedby="Saves and downloads agreements to browser 'downloads' folder as one or more XLSX files" 
                    @click="exportXLSX()"
                    @keydown.enter="exportXLSX()">Save as Excel XLSX</button>
                <button class="_main_button"       type="button" 
                    tabindex="0"
                    aria-label="Export agreements in JSON file" 
                    aria-decribedby="Saves and downloads agreements to browser 'downloads' folder as JSON file"
                    @click="exportJSON()"
                    @keydown.enter="exportJSON()">Save as JSON</button> 
                <button class="_main_button"       type="button" 
                    tabindex="0"
                    aria-label="Export agreements in JSON file" 
                    aria-decribedby="Saves and downloads agreements to browser 'downloads' folder as JSON file"
                    @click="renderState = 'LIST'"
                    @keydown.enter="renderState = 'LIST'">Cancel</button>   
            </div>
        </div>

        <div class="_agreements_controls">
            <br/>
            <div class="_agreements_inline_controls" v-if="renderState == 'VIEW'">
                <button class="_main_button"   type="button" 
                    tabindex="0"
                    aria-label="Return to agreements list" 
                    aria-describedby="Returns user to agreements list"
                    @click="backToList()"
                    @keydown.enter="backToList()">Back to List</button>
                <button class="_main_button"   type="button" 
                    tabindex="0"
                    aria-label="Generate PDF of agreement" 
                    aria-describedby="Generate PDF"
                    @click="generatePDF()"
                    @keydown.enter="generatePDF()">Create a PDF</button>
            </div>
            <div class="_agreements_inline_controls" v-if="(filteredAgreements.length > 0) && (renderState == 'VIEW' || renderState == 'EDIT')">
                <button class="_main_button" type="button" 
                    tabindex="0"
                    aria-lavel="Select next agreement in list" 
                    aria-describedby="Selects next agreement in list and displays it" 
                    @click="nextAgreement()"
                    @keydown.enter="nextAgreement()">Next </button>
            </div>
            <div class="_agreements_inline_controls" v-if="renderState == 'LIST'">
                <!-- 
                *** Importation of agreements is not implemented at this time ***
                <button class="_main_button" type="button" 
                    tabindex="0"
                    aria-label="Import agreements from file"
                    aria-describedby="Allows user to import agreements from JSON or CSV file"
                    @click="importAgreements()"
                    @keydown.enter="importAgreements()">Import Agreements </button>
                -->
                <button class="_main_button" type="button" 
                    tabindex="0"
                    aria-label="Export and download agreements" 
                    aria-describedby="Allows user to export agreements as a JSON or CSV file"
                    @click="exportAgreements()"
                    @keydown.enter="exportAgreements()">Export Agreements</button>  
             </div>

        </div>
        <!--   breaks to allow footer space -->
        <br/><br/>
    </div>
    
</template>

<script>
    import {ref, computed}              from 'vue'
    import {useStore}                   from 'vuex'
 
    import ViewAgreementEntire          from '@/components/Agreements/ViewAgreementEntire.vue'

    // import composables
    import useAgreementsAPI             from '@/composables/useAgreementsAPI'
    import useEmployeesAPI              from '@/composables/useEmployeesAPI'
    import useConfigAPI                 from '@/composables/useConfigAPI'
    import useDateHelpers               from '@/composables/useDateHelpers'
    import useRefreshAPI                from '@/composables/useRefreshAPI'
    import usePermissionsAPI            from '@/composables/usePermissionsAPI'
    import * as XLSX                    from 'xlsx-js-style'   

    // import enumerations
    import SysConfigs                   from '@/configuration/form-options.json'
 
    // pdf converter  install as npm install --save dom-to-pdf
    import domToPdf                     from 'dom-to-pdf'

    export default {
        components: {
            ViewAgreementEntire
        },
        props: [   
            'claims',
            'gat'
        ],
        emits: [     
        ],
    
        setup(props)    {
            const { agreeResults, fetchAgreementsOnQuery, fetchAgreementsOnOrgs} = useAgreementsAPI()
            const store                     =   useStore()
            const { checkStoreForRefresh }  =   useRefreshAPI()
            const { checkAdminGroups }      =   usePermissionsAPI()

            //  store data
            const user                  =   computed( () => store.state.user)
            const config                =   computed( () => store.state.config)
 
            const systemAdministrator   =   computed( () => store.state.user['is-sys-admin'])
            const deptAdministrator     =   computed( () => store.state.user['is-dept-admin'])
            const administrator             =   computed( () => {
                return systemAdministrator.value | deptAdministrator.value 
            })

            const sysConfigs            =   ref([])
            sysConfigs.value            =   SysConfigs
 
            const agreements            =   ref([])
            const agreement             =   ref({})
            const employee              =   ref({})
            const backupAgreement       =   ref({})
            const aExportableAgreements =   ref([])

            const viewIndex             =   ref(0)   
            const orgDisplay            =   ref(null)
            const saveFlag              =   ref(false)
            
            const processSelector       =   ref(null)
            const selectedFilename      =   ref("")

            const filters               =   ref({
                'number':           "",
                'employee':         "",
                'org':              "",
                'date':             "",
                'state':            ""
            })

            // MARK:  refresh store from database if global browser refresh fubars the store
            checkStoreForRefresh()
            .then( async () => {
                // Now that user has been estabished/re-established, fetch all agreements allowed to user
                // defer until promise resolved
                let aOrgs = Array.prototype.slice.call(user.value['allowed-orgs'])

                orgDisplay.value = user.value['allowed-orgs'].toString()

                if(user.value['is-sys-admin'])  {
                    await fetchAgreementsOnQuery({})
                    .then( () =>    {
                        agreements.value = agreeResults.value 
                    })
                }
                else {
                    await fetchAgreementsOnOrgs(aOrgs)
                    .then( () => {
                        agreements.value = agreeResults.value 
                    })
                } 
            })
            checkAdminGroups(props.claims, store)

            const filteredAgreements = computed( () => {

                let aFilteredValues = agreements.value
                aFilteredValues = aFilteredValues.filter( (agree) =>  {
                    return agree['number'].toLowerCase().match(filters.value['number'].toLowerCase())
                })  
                aFilteredValues = aFilteredValues.filter( (agree) =>  {
                    return agree['employee-name'].toLowerCase().match(filters.value['employee'].toLowerCase())
                })
                aFilteredValues = aFilteredValues.filter( (agree) =>  {
                    return agree['applicable-org'].toLowerCase().match(filters.value['org'].toLowerCase())
                })
                aFilteredValues = aFilteredValues.filter( (agree) =>  {
                    return agree['date-submitted'].toLowerCase().match(filters.value['date'].toLowerCase())
                })
                aFilteredValues = aFilteredValues.filter( (agree) =>  {
                    return agree['asset-state'].toLowerCase().match(filters.value['state'].toLowerCase())
                })
                return aFilteredValues
            })
 
            return {user, config, agreements, employee, agreement, sysConfigs, filters,
                    viewIndex, orgDisplay, filteredAgreements, aExportableAgreements,
                    processSelector, selectedFilename, backupAgreement, 
                    saveFlag, administrator}
        },

        data() {
            return {
                // switch for page conditional rendering        
                renderState: 'LIST',
                savedFlag:          false,
                filepath:           "",
                filename:           "",
                datafile:           "",
                bIncludeArrayProps: false,
                aExportFormats:  [
                    "consolidated",
                    "individual"
                ],
                sExportFormat:   "consolidated",
                agreeImportType:    "agreements"

            }
        },
        methods: {
    
            // FUNCTION: converts an ISO date (YYYY-MM-DDTHH:MM:sSS.SSSZ) to human readable
            prettyDate(date)     {
                const {makePrettyDate}      =   useDateHelpers()
                return makePrettyDate(date)  
            },
            // Function: converts status enumeration to more readable 
            prettyStatus(s) {
                s = s.replaceAll('_',' ')
                let aW = s.toLowerCase().split(' ')
                for (let i = 0; i < aW.length; i++) {
                    aW[i] = aW[i][0].toUpperCase() + aW[i].substr(1);
                }
                return aW.join(' ')
            },
            async viewAgreement(idx) {

                const {fetchEmployeesOnQuery, emplResults} = useEmployeesAPI()

                await fetchEmployeesOnQuery({"id":this.filteredAgreements[this.viewIndex]['fk-employee-id']})
                .then( () => {
                    this.employee = emplResults.value[0]
                    this.viewIndex = idx

                    // set agreement and copy (non-reactive) for backup operation
                    this.agreement          =   this.filteredAgreements[this.viewIndex]
                    this.backupAgreement    =   JSON.parse(JSON.stringify(this.filteredAgreements[this.viewIndex]))
                    this.renderState        =   "VIEW"
                })
            },
            // MARK:  THE FOLLOWING METHODS ARE APPLIED TO VIEW/EDIT ELEMENTS TO RETURN TO OVERALL LIST - GENERAL USERS
            backToList() {

                this.renderState    =   "LIST"
            },
            // FUNCTION: Disregards any edits and retores the agreement to a new agreeement (PENDING) state
            cancel()   {
                // return form to pristine state and disregard edits
                this.agreement          =   this.backupAgreement 
                this.viewIndex          =   0
                this.renderState        =   "LIST"
            },
            // FUNCTION: increments through viewing index (experimental)
            nextAgreement() {
                this.viewIndex++
                if (this.viewIndex >= this.filteredAgreements.length) this.viewIndex = 0
             },

            //MARK:  filter related fuctions
            clearFilters()  {
                this.filters   =   {
                    'number':           "",
                    'employee':         "",
                    'org':              "",
                    'date':             "",
                    'state':            ""
                 }
            },
            setFilters()  {
                // deprecated
            },
            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
            // Data Import Processes
            // Button triggers render switch to IMPORT rendering
            // Option buttons are presented
            // User clicking option button will requets file name for files or go immediately to API for Banner
            // For file imports, a file type state is set and the 'FILE' page is rendered.
            // Specified file is accessed and processing in switched on processSelector 
            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> 
            importAgreements()   {
                console.log("LE:: Import agreements fired")
                this.renderState = 'IMPORT'
            },
            // Function is triggered on file name submit on rendered page
            importSelection(event) {
                this.selectedFilename = event.target.value

                switch(this.processSelector) {
                    
                    case "JSON":
                        this.processorSelector = null
                        const JSONFileInput = document.querySelector("input[type=file]")
                        JSONFileInput.addEventListener("change", this.processJSONImport(JSONFileInput))
                        break;

                    case "CSV":
                        this.processorSelector = null
                        const CSVFileInput = document.querySelector("input[type=file]")
                        CSVFileInput.addEventListener("change", this.processCSVImport(CSVFileInput))
                        break;

                    case "XLSX":
                        this.processorSelector = null
                        const CSLSXFileInput = document.querySelector("input[type=file]")
                        CSLSXFileInput.addEventListener("change", this.processXLSXImport(CSLSXFileInput))
                        break;

                    default:
                        break;
                }
            },
         
            selectJSONFile() {
                this.processSelector = 'JSON'
                this.renderState = 'FILE'
            },
            selectCSVFile()    {
                this.processSelector = 'CSV'
                this.renderState = 'FILE'
            },
            selectXLSXFile() {
                this.processSelector = 'XLSX'
                this.renderState = 'FILE'
            },
            async processJSONImport(fileInput) {

                const {agreeResults, agreeKeyData, agreeStoreResponse, fetchAgreementsOnQuery, fetchAgreementsOnOrgs, 
                    storeAgreement, storeAllAgreements} = useAgreementsAPI()

                const [file] = fileInput.files
                var rawJsonData = ""
                if (file) {
                    rawJsonData = await file.text()
                    const aJsonData = JSON.parse(rawJsonData)
                    let resolvedPromises = await Promise.allSettled(aJsonData.map(oAgree => {
                            storeAgreement(oAgree)
                            return agreeKeyData.value[0]
                        }
                    ))
                    // have an array of settled promised with either resolve or response callbacks
                    resolvedPromises.forEach((rp, _)  =>  {
                        if(rp.status == 'rejected') {
                            //throw Error ("Axios promise rejected: fetching orgcode: "+oEmpl[num])
                        }
                        else if(rp.status == 'fulfilled')  {
                            // data available on promise
                            const keyData = rp.value.data      
                            
                            // do some more detailed error checking on API return
                            // data is JSON
                            if(!keyData.message.includes('success')) { 
                                console.log("StoreAgreement:: error thrown in storing agreements")
                            }
                            // completed and valid query returns 
                        }
                    })
                    .then( () =>    {
                        // refetch employees just loaded for list
                        // loaded employees may have disparate orgs but can see only allowed orgs
                        fetchAgreementsOnOrgs([this.aOrgs])
                        return agreeResults.value
                    })
                    .then( (fetchedAgreements)   =>  {
                        this.agreements = fetchedAgreements
                        this.renderState = 'LIST'
                    })
                }   
            },
            // May be importing different spreadsheets based on employeeImportType which can be
            // employees | contacts | allowed-orgs
            // Loading employees is straight forward.   Import CSV, generate employee object and store on Promise.all.
            // For contacts, CSV must have netid key.  Key is extracted and remaining row data used as contact attributes
            // For each mutator which consists of a contact array and netid, the employee is fetched on netid, UUID extracted,
            // and employee object created using UUID and contacts array.  This will 'update on dupicate key' the database
            // record.
            // Allowed orgs is handled the same way
            //
            async processCSVImport(fileInput)  {

                const {agreeResults, agreeKeyData, agreeStoreResponse, fetchAgreementsOnQuery, fetchAgreementsOnOrgs, 
                    storeAgreement, storeAllAgreements} = useAgreementsAPI()

                const [file] = fileInput.files
                var rawData = ""
                const loadableObjects = []

                if (file) {
                    rawData = await file.text()
                    const CSVData = rawData.replaceAll('\r','').split('\n') // split by lines

                    // each row of CSVData is comma delimited set of attributes
                    // first row is array keys - these must be translatable into API employee dictionary keys
                    // this is true for all components of employeeImportType
                    // NOTE: allowed orgs is simple:  {netid, org} as simple array  One value per row
                    //
                    const objKeys = CSVData[0].split(',')
                    
                    for( let i=1; i < CSVData.length-1; i++) {
                        const aObjAttributeSet = {}                // mutator objcet
                        var aAttributeSet = CSVData[i].split(',')
                        // iterate the attributes on each line converting them into a key: value set for store  
                        for (let j=0; j < aAttributeSet.length; j++) {
                            let key = objKeys[j]
                            aObjAttributeSet[key] = aAttributeSet[j]
                        }
                        // object constructed. Add to loadables
                        loadableObjects.push(aObjAttributeSet)
                    }

                    //process the loadable object (per import type)
                    switch( this.agreeImportType)    {

                        case 'agreements':
                            let resolvedPromises = await Promise.allSettled(loadableObjects.map( oEmpl => {
                                storeEmployee(oEmpl)
                                .then( () => {
                                    return emplKeyData.value[0]
                                })     
                            }))
                            // have an array of settled promised with either resolve or response callbacks
                            resolvedPromises.forEach((rp, _)  =>  {
                                if(rp.status == 'rejected') {
                                    //throw Error 
                                }
                                else if(rp.status == 'fulfilled')  {
                                    // data available on promise
                                    const keyData = rp.value.data      
                                    
                                    // do some more detailed error checking on API return
                                    // data is JSON
                                    if(!keyData.message.includes('success')) { 
                                        console.log("StoreEmployees:: error thrown in storing employees")
                                    }
                                    // completed and valid query returns 
                                }
                            })
                            .then( () =>    {
                                // refetch employees just loaded for list
                                // loaded employees may have disparate orgs but can see only allowed orgs
                                fetchEmployeesOnOrgs([this.aOrgs])
                                .then( () => {
                                    return emplResults.value
                                })  
                            })
                            .then( (fetchedEmpls)   =>  {
                                this.employees = fetchedEmpls
                                this.renderState = 'LIST'
                            })
                            break

 

                        case 'assets':
                            // imports assets from CSV and creates set objects, e.g. each asset will have a
                            // key of the agreement number and some asset attributes - one row per asset.  
                            // There may be multiple rows. 
                            //
                            //  Note: store will overwrite old assets. Be sure to include any existing assets
                            //
                            let aAssetMutators    =   []            // array of mutator objects for all listed agreements
                            loadableObjects.forEach( (o) => {
                                let mutator =  {                    // each agreement will have a mutator
                                    "number":    "",
                                    "assets": []
                                }
                                let idx = aAssetMutators.findIndex( (amo) => {
                                    amo.number === o.number
                                }) 
                                if(idx == -1) {
                                    // number is not in list of mutators so add it and the asset attributes
                                    mutator.number = o.number
                                    mutator.assets.push(delete o.number)   // delete number and push the rest
                                    aAssetMutators.push(mutator)
                                }
                                else {
                                    // mutator with agreement number exists so just add an asset
                                    aAssetMutators[idx].assets.push(delete o.number)

                                }
                            })
                            // result is a set of asset mutators - identify stored employee and update contacts
                            // Form:  [
                            //          { number: nn1, assets: []},
                            //          { number: nn2, assets: []}
                            //        ]
                            //
                            let resolvedOrgPromises = await Promise.allSettled(aAssetMutators.map( mutator => {
                                fetchAgreementsOnQuery({'number': mutator.number}) 
                                .then ( () => {
                                    return {
                                        // have to do this because unique key is uuid not number
                                        "uuid":emplResults.value[0].uuid,   // number makes return unique
                                        "assets": mutator.assets
                                    }    
                                })      
                            }))
                            // have an array of settled promises with either resolve or response callbacks
                            resolvedOrgPromises.forEach((rp, _)  =>  {
                                if(rp.status == 'rejected') {
                                    //throw Error ()
                                }
                                else if(rp.status == 'fulfilled')  {
                                    // data available on promise
                                    const agree = rp.value.data      
                                    storeAgreement(agree)
                                    .then( () => {
                                        return agreeKeyData.value[0]
                                    })  
                                }   
                            })
                            .then( () =>    {
                                // refetch employees just loaded for list
                                // loaded employees may have disparate orgs but can see only allowed orgs
                                fetchAgreementsOnOrgs([this.aOrgs])
                                .then( () => {
                                    return agreeResults.value
                                })     
                            })
                            .then( (fetchedAgreements)   =>  {
                                this.agreements = fetchedAgreements
                                this.renderState = 'LIST'
                            })
                            break                    }
                    
                }
                // back to the barn
                this.renderState = 'LIST'
            },

            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
            // Data Export Processes
            // Button triggers render switch to EXPORT rendering
            // Option buttons are presented
            // User clicking option button will generate file and download to braowser 'downloads'
            // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> 
            exportAgreements()  {
                //console.log("LE:: Export employees fired")
                this.renderState = 'EXPORT'
            },
            exportXLSX()    {
                // check for empty filteredlist
                if(this.filteredAgreements.length == 0)  {
                    this.renderState = 'LIST'
                    return
                }
                const workbook  =   XLSX.utils.book_new()

                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          SECTION TO GENERATE SPREADSHEET CELL HEADER VALUES
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

                // header generator Booleans - will be set true when found, otherwise will be excluded
                // from set reporting
                let bHaveAgreementHeaders   = false
                let bHaveAssetHeaders       = false
 
                // set up headers - pick up the first agreement [0] and strip headers
                // we cannot assume all agreements have subordinate records - we need to find a record with
                // a set of loaned assets 
                //
                // iterate the agreements to extract headers
                let aAgreeHeaders       =   []      // array of header keys
                let aAssetHeaders       =   []

                this.filteredAgreements.forEach( (a) =>    {
                    if(!bHaveAgreementHeaders) {
                        aAgreeHeaders = Object.keys(a ?? {}) 
                        if(aAgreeHeaders.length > 0 )   {
                            bHaveAgreementHeaders = true
                        }
                    }
                    if(!bHaveAssetHeaders) {
                        let aAssetItemKeys = Object.keys(a?.['loaned-assets'] ?? []) 
                        aAssetItemKeys.forEach( (i) =>  {
                            if(!bHaveAssetHeaders) {
                                aAssetHeaders = Object.keys(a?.['loaned-assets']?.[i] ?? {})
                                if(aAssetHeaders.length > 2 )   {
                                    bHaveAssetHeaders = true
                                }   
                            }
                        })
                    }
                })
             
                // MARK: - Remove array items from headers
                let propIndex = -1
                propIndex = aAgreeHeaders.indexOf('loaned-assets')
                if(propIndex != -1) aAgreeHeaders.splice(propIndex,1)
 
                // MARK: - Remove database specific FKs
                propIndex = aAgreeHeaders.indexOf('fk-employee-id')
                if(propIndex != -1) aAgreeHeaders.splice(propIndex,1)
                    propIndex = aAgreeHeaders.indexOf('id')
                if(propIndex != -1) aAgreeHeaders.splice(propIndex,1)

                 propIndex = aAssetHeaders.indexOf('fk-agreement-id')
                if(propIndex != -1) aAssetHeaders.splice(propIndex,1)
                    propIndex = aAssetHeaders.indexOf('fk-employee-id')
                if(propIndex != -1) aAssetHeaders.splice(propIndex,1)

                
                // MARK: - Check export format - if individual, insert headers for FA to associate records
                //         Use agreement-number as FA
                if(this.sExportFormat == 'individual')   {
                    // individual spreadsheet reporting - add foreign attributes (FA) as FK for association
                    if(bHaveAssetHeaders)       aAssetHeaders.unshift('agreement-number')

                }

                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          MAKE META RECORDS AT TOP OF SPREADSHEET
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                var currentSheet    =   []             //   array or rows
                var currentRow      =   []             //   array of cells
                let cRow            =   0              //   indexed to 0 in 'xlsx-js-style'
                let hdrRows         =   []             //   emphasized rows (headers)
                let boldRows        =   []             //   bolded data rows for agreement data (easier to differentiate)
                let sectionHeads    =   []             //   + components

                // generate date of report
                const date = new Date()
                let year    =   date.getFullYear().toString()
                let month   =   date.getMonth()+1
                month       =   month.toString().padStart(2,'0')
                let day     =   date.getDate()
                day         =   day.toString().padStart(2,'0')
                let hours   =   date.getHours().toString().padStart(2,'0')
                let mins    =   date.getMinutes().toString().padStart(2,'0')

                currentRow.push("Exported Loan Agreements")
                currentSheet.push(currentRow); cRow++
                currentRow = []
                currentRow.push(" ")
                currentRow = []
                currentRow.push("Date:")
                currentRow.push(month+'/'+day+'/'+year)
                currentSheet.push(currentRow); cRow++
                currentRow = []
                currentRow.push("Time:")
                let time = (hours >= 12)?(hours-12+":"+mins+" PM"):(hours+":"+mins+" AM")
                currentRow.push(time)
                currentSheet.push(currentRow); cRow++
                currentRow = []
                currentRow.push("Created by:")
                currentRow.push(this.user['name-concat'])
                currentSheet.push(currentRow); cRow++
                currentRow = []
                currentRow.push(" ")
                currentSheet.push(currentRow); cRow++
                currentSheet.push(currentRow); cRow++
                // end of meta data
                
                // create agreement header row
                currentRow = []
                aAgreeHeaders.forEach( (hdr) => {
                    currentRow.push(hdr)
                })
                // bold the row following (pre-increment cRow)
                hdrRows.push(cRow)
                currentSheet.push(currentRow); cRow++


                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          ITERATE FILTERED AGREEMENTS CONVERTING AGREEMENTS TO SHEET ROWS
                //
                //          If 'consolidated' reporting, intersperse array prop records
                //          else just do agreements and iterate filteredAgreements again later
                //          for each array prop type as separate sheet/tab
                //          Use:
                //              Generates array of arrays (array of rows)
                //              XLSX.utils.aoa_to_sheet(aoa) for data
                //              XLSX.utils.book_append_sheet for loaned-assets 
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                currentRow = []
                this.filteredAgreements.forEach( (_,i) => {
                    aAgreeHeaders.forEach((hdr) =>    {
                        currentRow.push(this.filteredAgreements[i]?.[hdr])
                    })
                    // conditionally format on consolidated sheets
                    if(this.bIncludeArrayProps && this.sExportFormat == 'consolidated') boldRows.push(cRow)
                    currentSheet.push(currentRow); cRow++
                    currentRow = []

                    // check for array props (if consolidated format)
                    if(this.bIncludeArrayProps && this.sExportFormat == 'consolidated') {
                        if(this.filteredAgreements[i].hasOwnProperty('loaned-assets')) {
                            if(this.filteredAgreements[i]?.['loaned-assets'].length > 0)  {
                                // render assets marker
                                currentRow.push("+")
                                currentRow.push("Loaned Assets")
                                sectionHeads.push(cRow)
                                currentSheet.push(currentRow); cRow++
                                currentRow = []
                                // render asset headers
                                aAssetHeaders.forEach( (hdr) => {
                                    currentRow.push(hdr)
                                })
                                // bold the row following (pre-increment cRow)
                                hdrRows.push(cRow)
                                currentSheet.push(currentRow); cRow++
                                currentRow = []
                                // now iterate the assets
                                this.filteredAgreements[i]['loaned-assets'].forEach( (asset) => {
                                    aAssetHeaders.forEach((hdr) =>    {
                                        currentRow.push(asset[hdr])
                                    })
                                    currentSheet.push(currentRow); cRow++
                                    currentRow = []
                                })
                            }
                        }
                        
                        // finished with all subordinate array props so return to agreement row processing
                        currentRow.push("-")
                        sectionHeads.push(cRow)
                        currentSheet.push(currentRow); cRow++
                        currentRow = []
                    }
                    // continue iterating agreements
                })

                // build the workbook with current data    
                var worksheet = XLSX.utils.aoa_to_sheet(currentSheet)

                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //  
                //          APPLY FORMATTING TO SHEET
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                // format primary 'Agreements' tab
                // !cols is an array of column format data used in xlsx-js-style - presumed objects {}
                // the maximum number of columns is the maximum length of the length of the headers array
                const sheetColumns = Math.max(aAgreeHeaders.length, aAssetHeaders.length)
                // maximum column width is established because of text entries in agreements.  Data must be
                // folded.  Minimum width provides for spacing for " " entries
                const maxWidth = 40
                const minWidth = 5

                // initialize style array to minWidth
                let aColStyles = []
                for (let i = 0; i < sheetColumns; i++) {
                    let oColArg    =    {
                        wch: minWidth
                    }
                    aColStyles.push(oColArg)
                }
                
                // iterate on array determined the maximum width of a column for cRow rows.
                for (let i = 0; i < cRow; i++)  {
                    // how many elements in the ith row array of the sheet array
                    if(currentSheet?.[i]?.length >= 1)   {
                        // iterate over the elements measuring the length (width precursor)
                        //console.log("Column iterations to be done: "+currentSheet?.[i]?.length)
                        for (let j = 0; j < currentSheet?.[i]?.length; j++)  {
                            let element = currentSheet?.[i]?.[j]
                            // if null record coalesce to default width
                            let elWidth = element?.length ?? minWidth
                            // restrict cell to maximum width
                            elWidth = (elWidth > maxWidth)?maxWidth:elWidth
                            aColStyles[j].wch = Math.max(elWidth, aColStyles[j].wch)  
                        }
                    }
                }

                // MARK: Apply format of column width
                worksheet["!cols"] = aColStyles

                // run cell styler
                for (let i in worksheet) {
                    if (typeof(worksheet[i]) != "object") continue;
                    let cell = XLSX.utils.decode_cell(i);

                    // MARK: set global styles
                    worksheet[i].s = {
                        font:   {
                            sz:     "10",
                            color:  {rgb: "000000"}
                        },
                        alignment:  {
                            horizontal:     "left",
                            wrapText:       true
                        }
                    }
                    if(cell.r == 0) {
                        // first row
                        worksheet[i].s = {
                            font:   {
                                sz:     "12",
                                color:  {rgb: "0000FF"},
                                bold: true
                            },
                        }
                    }
                    if(hdrRows.includes(cell.r))   {
                        // header rows highlighted
                        worksheet[i].s = {
                            //fill: {
                            //    patternType:    "solid",
                            //    bgColor:        {rgb: "090909"}      
                            //},
                            font: {
                                sz:             "11",
                                bold:           true,
                                color:          {rgb: "0000FF"}
                            }
                        }
                    }
                    if(boldRows.includes(cell.r))   {
                        // agreement data bolded
                        worksheet[i].s = {
                            font: {
                                bold:           true
                            }
                        }
                    }
                    if(sectionHeads.includes(cell.r)) {
                        worksheet[i].s = {
                            font:   {
                                sz:     "11",
                                color:  {rgb: "999900"},
                                bold: true
                            }
                        }
                    }
                }
 
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          CONSTRUCT THE XLSX WORKBOOK 
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Loan Agreements');

                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //
                //          IF INDIVIDUALIZED FORMAT APPEND INDIVIDUAL WORKSHEETS TO WORKBOOK
                //
                // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                //MARK:  Now see if user wants array props
                if(this.bIncludeArrayProps && this.sExportFormat == 'individual') {

                    var currentAssetSheet       =   []             //   array of rows
                    let cAssetRow               =   0               
                    let assetHdrRows            =   []
                   
                    // build the individualized worksheet headers
                    if(aAssetHeaders.length > 0) {
                        currentRow = []
                        // construct header row
                        aAssetHeaders.forEach( (hdr) => {
                            currentRow.push(hdr)
                        })
                        // bold the row following (pre-increment cRow)
                        assetHdrRows.push(cAssetRow)
                        currentAssetSheet.push(currentRow); cAssetRow++
                    }

                    // MARK:  Now iterate the set of filtered Agreements extracting
                    //        schedule, asset, and comm method info     
                    //
                    this.filteredAgreements.forEach( (_,i) => {
                        // sometimes agreements have no array properties - guard against this

                        if(this.filteredAgreements[i].hasOwnProperty('loaned-assets')) {
                            if(this.filteredAgreements[i]['loaned-assets'].length > 0)  {
                                
                                // iterate the assets on the agreement
                                this.filteredAgreements[i]['loaned-assets'].forEach( (asset) => {
                                    currentRow = []
                                    // first header is FA as association key to agreement (agreement-number)
                                    currentRow.push(this.filteredAgreements[i]['number'])
                                    // skip 0th element (number)
                                    for (let j=1; j < aAssetHeaders.length; j++) {
                                        currentRow.push(asset[aAssetHeaders[j]])
                                    }
                                    currentAssetSheet.push(currentRow); cAssetRow++
                                })
                            }
                        }
                    
                        // continue iterating agreements
                    })  

                    // MARK: Create the auxillary worksheets
                    var worksheetAssets     = XLSX.utils.aoa_to_sheet(currentAssetSheet)

                
                    // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                    //  
                    //          APPLY FORMATTING TO AUXILARY SHEETS
                    //              LOANED ASSETS TAB
                    //
                    // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
                    // MARK:  Do the width array setup
                    // count of asset headers 
                    const assetHdrs = aAssetHeaders?.length 
                    // initialize style array to minWidth
                    let aAssetColStyles = []
                    for (let i = 0; i < assetHdrs; i++) {
                        let oColArg    =    {
                            wch: minWidth
                        }
                        aAssetColStyles.push(oColArg)
                    }
                    // MARK: - Run determination of maximum column width for each tab
                    
                    // iterate on array and determine the maximum width of a column for number of rows.
                    for (let i = 0; i < cAssetRow; i++)  {
                        // how many elements in the ith row array of the sheet array
                        if(currentAssetSheet?.[i]?.length >= 1)   {
                            // iterate over the elements measuring the length (width precursor)
                            for (let j = 0; j < currentAssetSheet?.[i]?.length; j++)  {
                                let element = currentAssetSheet?.[i]?.[j]
                                // if null record coalesce to default width
                                let elWidth = element?.length ?? minWidth
                                // restrict cell to maximum width
                                elWidth = (elWidth > maxWidth)?maxWidth:elWidth
                                aAssetColStyles[j].wch = Math.max(elWidth, aAssetColStyles[j].wch)  
                            }
                        }
                    }
 
                    // MARK: Apply format of column width to all sheets
                    worksheetAssets["!cols"]    = aAssetColStyles
 
                    // apply on a per-cell basis the rest of the formatting
                    for (let i in worksheetAssets) {
                        if (typeof(worksheetAssets[i]) != "object") continue;
                        let cell = XLSX.utils.decode_cell(i);

                        // MARK: set global styles
                        worksheetAssets[i].s = {
                            font:   {
                                sz:     "10",
                                color:  {rgb: "000000"}
                            },
                            alignment:  {
                                horizontal:     "left",
                                wrapText:       true
                            }
                        }
                        if(assetHdrRows.includes(cell.r))   {
                            // header rows highlighted
                            worksheetAssets[i].s = {
                                font: {
                                    sz:             "11",
                                    bold:           true,
                                    color:          {rgb: "0000FF"}
                                }
                            }
                        }
                    }
 
                    // MARK:  Append worksheets to workbook
                    XLSX.utils.book_append_sheet(workbook, worksheetAssets, 'Loaned-Assets');
                }   

                // 20240404_1559 format
                const filedate = year+month+day+'-'+hours+mins
                this.filename = "loan-agreements"+"-"+filedate+".xlsx"    

                XLSX.writeFile(workbook, this.filename, { compression: true, cellStyles: true });

                this.renderState = 'LIST'
                return
            },
            exportJSON() {

                // check for empty filteredlist
                if(this.filteredAgreements.length == 0)  {
                    this.renderState = 'LIST'
                    return
                }
                // convert employees to JSON datafile
                this.datafile = JSON.stringify(this.filteredAgreements)

                var blobData = []
                blobData.push(this.datafile)

                const date = new Date()
                let year    =   date.getFullYear().toString()
                let month   =   date.getMonth()+1
                month       =   month.toString().padStart(2,'0')
                let day     =   date.getDate()
                day         =   day.toString().padStart(2,'0')
                let hours   =   date.getHours().toString().padStart(2,'0')
                let mins    =   date.getMinutes().toString().padStart(2,'0')

                // 20240404_1559 format
                const filedate = year+month+day+'-'+hours+mins
                this.filename = "loan-agreements"+"-"+filedate+".json"    

                var textFileAsBlob =  new Blob(blobData, {type:'application/json'});
                //var saveAsFilename = document.getElementById("_saveAs").value;

                var downloadLink = document.createElement("a")
                downloadLink.setAttribute("id","_pseudoID")
                
                downloadLink.download = this.filename
                downloadLink.innerHTML = "_"
                if (window.webkitURL != null) {
                    // Chrome allows the link to be clicked
                    // without actually adding it to the DOM.
                    downloadLink.href = window.webkitURL.createObjectURL(textFileAsBlob)
                }
                else  {
                    // Firefox requires the link to be added to the DOM
                    // before it can be clicked.
                    downloadLink.href = window.URL.createObjectURL(textFileAsBlob)
                    downloadLink.onclick = document.body.removeChild(target)
                    downloadLink.style.display = "none"
                    document.body.appendChild(downloadLink)
                }
                downloadLink.click()
                window.URL.revokeObjectURL(textFileAsBlob)
                downloadLink.remove()

                // back to the barn
                this.renderState = 'LIST'
            },
            // FUNCTION: Created a PDF of ViewAgreementEntire rendering
            // id of element on DOM is '_pdf_render'
            // Need to created from the DOM and then do a standard download so that the PDF appears
            // in the browser downloads.
            async generatePDF() {
                
                // MARK:  generate the appropriate filename with embedded date
                const date = new Date()
                let year    =   date.getFullYear().toString()
                let month   =   date.getMonth()+1
                month       =   month.toString().padStart(2,'0')
                let day     =   date.getDate()
                day         =   day.toString().padStart(2,'0')
                let hours   =   date.getHours().toString().padStart(2,'0')
                let mins    =   date.getMinutes().toString().padStart(2,'0')

                 // 20240404_1559 format
                 const filedate = year+month+day+'-'+hours+mins
                 let agreeNumber = this.agreement['number']
                
                // MARK: - Since this is a list of multiple employees, we want to 
                // identify the agreement document with the employee's last name in
                // addition to the agreement number.  This is available in string form and we
                // want only the last name
                let aNames = this.agreement['employee-name'].split(" ")
                let lName = aNames[aNames.length-1]
                const pdfFileName = lName+"-Loan-Agreement-"+agreeNumber+"-"+filedate+".pdf"    

                // select and the container and add a title for teh PDF document              
                var element = document.getElementById('_pdf_render');
                var title   = document.createElement("p")
                var brk     = document.createElement("br")
                title.textContent = "Equipment Loan Agreement"
                title.style['font-family'] = "'Times New Roman',Times, serif"
                title.style['font-size'] = "16pt"
                title.style['font-weight'] = "bold"
                title.style['text-align'] = "center"
                title.appendChild(brk)
                title.appendChild(brk)
                var firstChild = document.getElementById("_pdf_row1")
                element.insertBefore(title,firstChild)

                var options = {
                    filename: pdfFileName,
                    excludeTagNames: ['button']
                };
                await domToPdf(element, options, function(pdf) {
                     
                    title.remove()
                });
                
                
            }

        },

        // hooks
        beforeMount() {

        },
        mounted() {
           
        },
        beforeUpdate() {


        },
        updated() {
            //console.log("LA:: Updated: user netid: "+this.user['netid'])
            //console.log("LA:: Updated: viewIndex: "+this.viewIndex)
            //console.log("LA:: Updated:: userRoles: "+this.userRoles)
            //console.log("LA:: Updated:: enSupervisorNotes: "+this.enSupervisorNotes)
            //console.log("LA:: Updated:: enApproverNotes: "+this.enApproverNotes)
        }
    }
</script>

<style scoped>

    ._master_container {
        font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
        min-height: 50vh;
        margin: 25px 0 0;
        padding: 0;
    }
 
    ._list_container {
        width: 98%;
        overflow-x: auto;
        overflow-y: auto;
        margin: 0 5px;;
        padding: 0 5px;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: rgb(149, 182, 202);
    }
    ._export_container {
        width: 98%;
        overflow-x: auto;
        margin: 0;
        padding: 0;
    }
  
    ._list {
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._agree_view {
        width: 90%;
        margin: 0 auto 0;
        padding: 2px;
    }
    ._p_page_header {
        width: 50%;
        margin: 10px auto;
        padding: 0 5px;
        color: #13294B;
        font-size: 12pt;
        font-weight: bold;
        text-align: center;
    }
    ._p_list_header {
        margin: 0;
        padding: 2px 0;
        color: #13294B;
        font-size: 0.9em;
        font-weight: bold;
        text-align: left;
    }
    ._p_list_actions_header {
        margin: 0;
        padding: 2px 0;
        color: #13294B;
        font-size: 0.9em;
        font-weight: bold;
        text-align: center;
    }
    ._p_export {
        width: 50%;
        margin: 25px auto;
        padding: 2px;
        color:#006230;
        font-size: 1.0em;
        font-weight: normal;
        text-align: justify;
    }
    ._type_list {
        font-size: 0.9em;
        margin: 0 5px;
        padding: 2px;
    }
    ._input_label {
        margin: 0;
        padding: 0 10px 0 0 ;
    }
    ._select {
        padding: 2px;
    }
    ._p_import {
        width: 50%;
        margin: 25px auto;
        padding: 2px;
        color:#006230;
        font-size: 1.0em;
        font-weight: normal;
        text-align: justify;
    }
    ._p_import_center {
        width: 100%;
        margin: 10px auto 0;
        padding: 2px;
        color:#006230;
        font-size: 1.0em;
        font-weight: normal;
        text-align: justify;
    }
    ._p_import_right {
        width: 100%;
        margin: 0;
        padding: 0;
        color:#006230;
        font-size: 1.0em;
        font-weight: normal;
        text-align: center;
    }
    ._file_selector {
        margin: 2px auto;
        text-align: center;
        background-color: #13294B;
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        color: white;
        font-size: 1.0em;
        font-weight: normal;
    }
 
    /* list action button styling */
    ._action_button {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:#13294B;
        color: white;
        margin: 0 5px;
        font-size: 0.8em;
    }
    ._action_button:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._action_button_revoke {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:red;
        color: white;
        margin: 0 5px;
        font-size: 0.8em;
    }
    ._action_button_revoke:hover {
        background-color: white;
        color: red;
    }
   
    ._agreements_controls {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 15px 0;
        padding: 5px;
    }
    ._agreements_inline_controls {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin: 0;
        padding: 0;
    }
    ._export_controls {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 5px;
    }
    ._export_check {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2px;       
    }
    ._export_rules {
        margin: 0;
        padding: 2px 5px;
    }
    ._check {    
        margin: 0;
        padding: 0 6px;
    }
    ._check_label {
        margin: 0;
        padding: 0 6px;
    }
    /* agreements list styling */
    ._agreements_table {
        width: 100%;
        display: table;
    }
    ._filters_table {
        display: table;
    }
  
    ._agreements_table_row   {
        background-color:#BFC9D9;
        display: table-row;
        min-width: 40px;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        overflow: hidden;
        height: 25px;
        white-space: nowrap;
    }

    ._agreements_table_row:nth-child(2n+2) {
        display: table-row;
        min-width: 40px;
        text-align: center;
        margin: 0;
        background-color:#a4b9dc;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
    }
    ._agreements_table_cell  {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        margin: 0 0 0 20px;
        padding: 2px 0 2px 10px;
    }
    ._agreements_table_link_cell  {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        color: #13294B;
        text-decoration: none;
        margin: 0 0 0 20px;
        padding: 2px 0 2px 10px;
    }
    ._agreements_table_link_cell:hover  {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        color: #FF5F05;
        text-decoration: none;
        margin: 0 0 0 20px;
        padding: 2px 0 2px 10px;
    }
    ._agreements_table_cell_ck  {
        display: table-cell;
        text-align: center;
        padding: 2px 2px;
    }
    
    ._filters_table_cell  {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 10px;
    }
    ._agreement_actions_table_cell {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content:center;
        
    }
    ._agreement_actions_table_cell_header {
        display: table-cell;
        min-width: 40px;
        text-align: center;
    }
    ._p_column_headers {
        font-size: 1.0em;
        font-weight: bold;
        color: #13294B;
        text-align: center;
        margin: 2px 0;
        padding: 5px;
    }
 
    ._input_filter {
        width: 70%;
        background-color: white;
        color: black;
        font-size: 0.9em;
        font-weight: normal;
        border-style: solid;
        border-radius: 3px;
        border-width: 3px;
        border: #13294B;
        margin: 0 3px 0;
        padding: 2px;
    }
    ._input_filter_en {
        float: left;
        margin: 0 0 0 20px;
        /*
        background-color: white;
        color: black;
        font-size: 0.9em;
        font-weight: normal;
        border-style: solid;
        border-radius: 3px;
        border-width: 3px;
        border: black;
        */
        margin: 0 0 0 10%;
        padding: 2px 0 2px 10px;
    }
    ._input_filter_ck {
        float: left;
        /*
        background-color: white;
        color: black;
        font-size: 0.9em;
        font-weight: normal;
        border-style: solid;
        border-radius: 3px;
        border-width: 3px;
        border: black;
        */
        margin: 0 3px 0;
        padding: 2px;
    }
    /*
    ._agreements_warning {
        text-align: center;
        color: #FF5F05;
        margin: 25px auto;
        padding: 5px;
    }
    */
    ._no_agreements_warning_container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: 20px;
        margin: 10px 0;
    }
    ._agreements_warning {
        color: #FF5F05;
    }
    _._agreements_warning_clear {
        float: right;
    }  
 
    /* general controls styling */
    ._button_back_list {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._button_back_list:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._button_next {
        background-color:#13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._button_next:hover {
        background-color: #FF5F05;
        color: white;
    }
    

    ._button_approve {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._button_approve:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._button_reject {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._button_reject:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._main_button_revoke {
        background-color: red;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-width: 1px;
        border-radius: 5px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._main_button_revoke:hover {
        background-color: white;
        color: red;
    }
    

    ._main_button {
        background-color:#13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._main_button:hover {
        background-color: #FF5F05;
        color: white;
    }   
    ._button_export {
        background-color:#006230;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border: black;
        margin: 5px 10px;
        padding: 5px;
    }
    ._button_export:hover {
        background-color: white;
        color: #006230;
    }

    ._button_clear_filters {
        background-color:#13294B;
        color: white;
        font-size: 0.9em;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 2px 10px;
        padding: 5px;
    }

    ._button_clear_filters:hover  {
        background-color: #FF5F05;
        color: white;
    }   
    ._button_export_1    {
        background-color:#13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._button_export_1:hover  {
        background-color: #FF5F05;
        color: white;
    } 
    ._button_export_2    {
        background-color:#13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._button_export_2:hover  {
        background-color: #FF5F05;
        color: blue;
    } 
    ._button_export_3    {
        background-color:#13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._button_export_3:hover  {
        background-color: #FF5F05;
        color: white;
    } 
    ._button_revoke_all {
        background-color:red;
        color: white;
        font-size: 0.9em;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 2px 10px;
        padding: 5px;
    }

    ._button_revoke_all:hover  {
        background-color: white;
        color: red;
    }   
    ._revocation_box {
        width: 50%;
        background-color: #f9da98;
        border-style: solid;
        border-width: 5px;
        border-color: #13294B;
        color: black;
        text-align: justify;
        padding: 8px;
        margin: 0 auto;
    }
    ._p_revoke {
        color: black;
    }
    ._button_revoke_confirmed {
        background-color:#13294B;
        color: white;
        font-size: 0.9em;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 2px 10px;
        padding: 5px;
    }
    ._button_revoke_confirmed:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._button_cancel_revocation {
        background-color:#13294B;
        color: white;
        font-size: 0.9em;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 2px 10px;
        padding: 5px;
    }
    ._button_cancel_revocation:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._import_fieldset {
        width: 100%;
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 0;
        padding: 0;
    }
    ._row {
        width: 70%;
        margin: 0 auto;
        text-align: center;
    }
    ._radio {
        display: inline-block;
        width: 16px;
        margin: 0;
        /* position: relative; 
        top: 2px; 
        */
    }
    ._item_label {
        display: inline-block;
        width: 100%; 
        font-size: 0.8em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
        margin: 0;
        padding: 0 0 0 10px;
    }
    ._col_30 {
        display: inline-block;
        width: 30%;
        margin: 0;
        padding: 0;
    }
    ._col_33 {
        display: inline-block;
        width: 33%;
        margin: 0;
        padding: 0;
    }
    ._col_67 {
        display: inline-block;
        width: 67%;
        margin: 0;
        padding: 0;
    }
    ._col_70 {
        display: inline-block;
        width: 70%;
        margin: 0;
        padding: 0;
    }
    ._scol_3 {
        display: inline-block;
        width: 3%;
        margin: 0 0 0 5px;
        padding: 0;
    }
    ._scol_15 {
        display: inline-block;
        width: 15%;
        margin: 0 0 0 5px;
        padding: 0;
    }
    ._scol_20 {
        display: inline-block;
        text-align: left;
        width: 20%;
        margin: 0;
        padding: 0 10px 0;
    }
    ._scol_25 {
        display: inline-block;
        text-align: left;
        width: 25%;
        margin: 0 auto;
        padding: 0;
    }
    ._scol_50 {
        display: inline-block;
        text-align: left;
        width: 50%;
        margin: 0 auto;
        padding: 0;
    }
    ._scol_80 {
        display: inline-block;
        text-align: left;
        width: 80%;
        margin: 0 auto;
        padding: 0;
    }
    ._scol_85 {
        display: inline-block;
        text-align: left;
        width: 85%;
        margin: 0 auto;
        padding: 0;
    }
    @media screen and (max-width: 800px) {
        ._import_fieldset {
            flex-wrap: wrap;  
        }
        ._col_30 {
            width: 100%;
        }
        ._col_33 {
            width: 100%;
        }  
        ._col_67 {
            width: 100%;
        }
        ._col_70 {
            width: 100%;
        }  
    }
    @media screen and (max-width: 620px) {
        ._col_33 {
            width: 100%;
            margin-top: 0;
        }
        ._col_30 {
            width: 100%;
            margin-top: 0;
        }
 
       ._scol_25 {
            width: 100%;
            text-align: center;
        }
        ._col_70 {
            width: 100%;
            margin-top: 0;
        }
        
    }
</style>